import React, { useState } from 'react';
import { VStack, Text, Input, Heading, Field } from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Button from '../../components/base/Button';
import { useTranslation } from '../../hooks/useTranslation';
import { useSendRegistrationEmailMutation, getHttpStatus } from '../../api';
import { createRegistrationEmailSchema, RegistrationEmailFormData } from '../../schemas/registrationSchema';

interface RegistrationEmailStepProps {
    onSuccess: (email: string) => void;
}

export const RegistrationEmailStep: React.FC<RegistrationEmailStepProps> = ({ onSuccess }) => {
    const { t } = useTranslation();
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [sendRegistrationEmail, { isLoading }] = useSendRegistrationEmailMutation();

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors, isValid },
    } = useForm<RegistrationEmailFormData>({
        resolver: yupResolver(createRegistrationEmailSchema(t)),
        mode: 'onChange',
        defaultValues: { email: '' },
    });

    const email = watch('email');
    React.useEffect(() => {
        if (submitError && email) setSubmitError(null);
    }, [email]);

    const handleFormSubmit = async (data: RegistrationEmailFormData) => {
        setSubmitError(null);
        try {
            await sendRegistrationEmail({ email: data.email }).unwrap();
            onSuccess(data.email);
        } catch (err) {
            const status = getHttpStatus(err);
            if (status === 400) {
                setSubmitError(t.registration.errors.invalidEmail);
            } else if (status === 429) {
                setSubmitError(t.registration.errors.rateLimit);
            } else {
                setSubmitError(t.registration.errors.serverError);
            }
        }
    };

    return (
        <>
            <VStack gap={2} align="center">
                <Heading size="lg" color="gray.800" textAlign="center">
                    {t.registration.title}
                </Heading>
            </VStack>

            <VStack gap={6} align="stretch">
                <Field.Root invalid={!!errors.email || !!submitError}>
                    <Field.Label fontSize="md" fontWeight="bold" color="gray.800" mb={3}>
                        {t.registration.emailLabel}
                    </Field.Label>
                    <Input
                        type="email"
                        placeholder={t.registration.emailPlaceholder}
                        {...register('email')}
                        size="lg"
                        h="48px"
                        borderRadius="0"
                        colorPalette={errors.email || submitError ? 'red' : 'gray'}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSubmit(handleFormSubmit)();
                            }
                        }}
                    />
                    {errors.email && (
                        <Field.ErrorText color="red.500" fontSize="sm" mt={1}>
                            {errors.email.message}
                        </Field.ErrorText>
                    )}
                    {submitError && !errors.email && (
                        <Field.ErrorText color="red.500" fontSize="sm" mt={1}>
                            {submitError}
                        </Field.ErrorText>
                    )}
                </Field.Root>

                <Button
                    type="button"
                    buttonType="success"
                    size="lg"
                    w="full"
                    disabled={!isValid || isLoading}
                    isLoading={isLoading}
                    onClick={handleSubmit(handleFormSubmit)}
                >
                    {t.registration.continueButton}
                </Button>
            </VStack>

            <VStack>
                <Text fontSize="sm" color="gray.600" textAlign="center">
                    {t.registration.haveAccount}{' '}
                </Text>
                <Link to="/login" style={{ fontWeight: 600, color: 'var(--chakra-colors-yellow-600)' }}>
                    {t.registration.signInLink}
                </Link>
            </VStack>
        </>
    );
};
